import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import { adaptFilters } from "./fullScheduleHelpers";

// Data + filter state for the senior full-schedule view. Filters start empty
// and the server fills the defaults (own location, today, day/trainers/all);
// from then on the effective values come back in the payload, so the controls
// are always in sync with what's on screen. Attendance and note edits are
// layered on optimistically and dropped whenever a fresh fetch arrives.
export default function useFullSchedule() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState({ view: "day", entityType: "trainer", entityId: "all", locationId: null, date: null });
  const [attendanceOverrides, setAttendanceOverrides] = useState({});
  const [clientNoteOverrides, setClientNoteOverrides] = useState({});

  const { data, loading, error } = useFetch(
    () =>
      api.getTrainerFullSchedule({
        view: filters.view,
        entityType: filters.entityType,
        entityId: filters.entityId,
        locationId: filters.locationId || undefined,
        date: filters.date || undefined
      }),
    [refreshKey, filters.view, filters.entityType, filters.entityId, filters.locationId, filters.date]
  );

  // A fresh fetch carries authoritative data; local optimistic edits layered on
  // the previous result are no longer needed. Cleared on arrival, not on request,
  // so a refresh never flashes the pre-edit statuses while it loads.
  useEffect(() => {
    setAttendanceOverrides({});
    setClientNoteOverrides({});
  }, [data]);

  // A refresh re-asks for the filters already on screen, so the current grid
  // stays up (keeping its scroll position) instead of being swapped for a spinner.
  const filtersKey = [filters.view, filters.entityType, filters.entityId, filters.locationId, filters.date].join("|");
  const [loadedFiltersKey, setLoadedFiltersKey] = useState(null);
  useEffect(() => {
    if (!loading && data) setLoadedFiltersKey(filtersKey);
  }, [loading, data, filtersKey]);
  const refreshing = loading && Boolean(data) && loadedFiltersKey === filtersKey;

  const hasOverrides = Object.keys(attendanceOverrides).length || Object.keys(clientNoteOverrides).length;
  const rawBookings = data?.bookings || [];
  const bookings = hasOverrides
    ? rawBookings.map((b) => ({
        ...b,
        clients: (b.clients || []).map((c) => ({
          ...c,
          status: attendanceOverrides[c.bookishClientId] || c.status,
          note: clientNoteOverrides[c.bookishClientId] !== undefined ? clientNoteOverrides[c.bookishClientId] : c.note
        }))
      }))
    : rawBookings;

  const roster = data?.roster || [];
  // Effective values: the user's explicit picks, else what the server resolved.
  const effective = {
    view: data?.view || filters.view,
    entityType: data?.entityType || filters.entityType,
    entityId: data?.entityId || filters.entityId,
    locationId: filters.locationId || data?.locationId || null,
    date: filters.date || data?.date || null
  };

  const apply = (patch) => setFilters((prev) => adaptFilters({ ...prev, ...effective }, patch, roster));

  const shiftDate = (direction) => {
    const anchor = effective.date || DateTime.now().toFormat("yyyy-MM-dd");
    const days = effective.view === "week" ? 7 * direction : direction;
    apply({ date: DateTime.fromISO(anchor, { zone: "UTC" }).plus({ days }).toFormat("yyyy-MM-dd") });
  };

  return {
    data,
    loading,
    refreshing,
    error,
    bookings,
    roster,
    filters: effective,
    setLocation: (locationId) => apply({ locationId }),
    setEntityType: (entityType) => apply({ entityType }),
    setEntityId: (entityId) => apply({ entityId: entityId || "all" }),
    setView: (view) => apply({ view }),
    setDate: (date) => apply({ date }),
    shiftDate,
    goToday: () => apply({ date: null }),
    refresh: () => setRefreshKey((n) => n + 1),
    applyAttendance: (updates) => setAttendanceOverrides((prev) => ({ ...prev, ...updates })),
    applyClientNote: (bookishClientId, note) => setClientNoteOverrides((prev) => ({ ...prev, [bookishClientId]: note }))
  };
}
